import classNames from "classnames";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import content from "../../../config/content.json";
import { useAccount } from "../../../hooks/useAccount";
import { useChat } from "../../../hooks/useChat";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import useModal from "../../../hooks/useModal";
import { usePaymentRequest } from "../../../hooks/usePaymentRequest";
import useScreenSize from "../../../hooks/useScreenSize";
import { useToken } from "../../../hooks/useToken";
import Avatar from "../../common/Avatar/Avatar";
import Button from "../../ui/Button/Button";
import {
	HeaderNavigation,
	MobileNavigation,
	NavItemProps,
} from "../Navigation/Navigation";
import "./Header.scss";

const Header = ({
	headerWithTransparentBackground,
}: {
	headerWithTransparentBackground?: boolean;
}) => {
	const { token } = useToken();
	const { account, signOut } = useAccount();
	const validAuth = token !== null && account !== null;
	const { hasUnreadMessage } = useChat();
	const { countUnpaidPaymentRequest } = usePaymentRequest();
	const { width } = useScreenSize();
	const isMobile = width < 992;
	const navigate = useNavigate();
	const {
		isOpen: isMobileNavigationOpen,
		close: closeMobileNavigation,
		open: openMobileNavigation,
	} = useModal();
	useLockBodyScroll({ toggle: isMobileNavigationOpen });
	const handleSignOut = () => {
		signOut();
		if (isMobileNavigationOpen) {
			closeMobileNavigation();
		}
		navigate(0);
	};
	const functionsAvailableFromConfig = { "Sign Out": handleSignOut };

	const renderCriteria: {
		DO_NOT_RENDER: boolean;
		isMember: boolean;
	} = useMemo(
		() => ({
			DO_NOT_RENDER: false,
			isMember: account ? account["isMember"] : false,
		}),
		[account],
	);

	/* Map of variables requested in the config file */
	const valuesAvailableFromConfig: { hasUnreadMessage: boolean } = useMemo(
		() => ({
			hasUnreadMessage,
		}),
		[hasUnreadMessage],
	);

	const getUpdatedLinks = useCallback(
		(originalLinks: NavItemProps[]) =>
			(originalLinks as NavItemProps[]).map((link) => {
				if (link?.requiredToRender || link?.additionalContentFrom) {
					return {
						...link,
						additionalContentFrom: link?.additionalContentFrom ? (
							(valuesAvailableFromConfig as { hasUnreadMessage: boolean })[
								link?.additionalContentFrom as keyof {
									hasUnreadMessage: boolean;
								}
							] && <div className="red-alert" />
						) : (
							<></>
						),
						render: link?.requiredToRender
							? renderCriteria[
									link?.requiredToRender as keyof typeof renderCriteria
								]
							: true,
					};
				}
				return link;
			}),
		[renderCriteria, valuesAvailableFromConfig],
	);

	const navLinks = useMemo(
		() => [
			...(!validAuth
				? getUpdatedLinks(content.header.signedOut.links as NavItemProps[])
				: getUpdatedLinks(content.header.signedIn.links as NavItemProps[])),
		],
		[getUpdatedLinks, validAuth],
	);
	const navActions = useMemo(
		() => [
			...(!validAuth
				? (content.header.signedOut.actions as NavItemProps[])
				: (content.header.signedIn.actions as NavItemProps[])),
		],
		[validAuth],
	);
	const socialLinks = content.header.social as NavItemProps[];

	return (
		<header
			className={`site__header${headerWithTransparentBackground ? " site-header--transparent" : ""}`}>
			<Link to={content.header.home.url} className="home-link">
				<img
					src="/imagenes/FlyORKA-logo.svg"
					alt={content.header.home.label}
					className="logo"
				/>
			</Link>
			{!isMobile ? (
				<HeaderNavigation
					links={navLinks}
					actions={navActions}
					functionsAvailableFromConfig={functionsAvailableFromConfig}
					validAuth={validAuth ?? false}
				/>
			) : (
				<Button
					className={classNames("mobile-nav__button mobile-nav__button--open", {
						"button--avatar": validAuth,
					})}
					onClick={openMobileNavigation}
					aria-label="Open Mobile Menu">
					{validAuth ? (
						<div className="avatar-container">
							<Avatar />
							{(countUnpaidPaymentRequest > 0 ||
								(isMobile && hasUnreadMessage)) && (
								<div className="red-alert" />
							)}
						</div>
					) : (
						<img
							src="/imagenes/menu-burger.svg"
							alt=""
							className="action-icon"
						/>
					)}
				</Button>
			)}
			{isMobile && isMobileNavigationOpen && (
				<MobileNavigation
					home={content.header.home}
					links={[
						...(validAuth ? [] : [{ label: "Home", url: "/" }]),
						...navLinks,
					]}
					actions={navActions}
					functionsAvailableFromConfig={functionsAvailableFromConfig}
					social={socialLinks}
					onClose={closeMobileNavigation}
				/>
			)}
		</header>
	);
};

export default Header;
